import { useEffect, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { trackPaywall, trialCtaFor } from '../utils/access';
import { openLanding } from '../utils/landing';
import { PLUSZ_PATH } from '../utils/pricing';
import './TrialEndedModal.css';

/* ══════════════════════════════════════════════════════════════════════════
   GOMBOS PRÓBA (Plusz 1. kör, B. rész)
   ─────────────────────────────────────────────────────────────────────────
   A regisztráció csak FREE-fiókot ad; a 14 napos PRO-próbát a felhasználó
   maga indítja, egyszer. A gomb csak annak látszik, akinek még nem volt
   próbája (trialCtaFor); a többit a start_trial() RPC dönti el, és az
   elutasítás okát itt írjuk ki. A szövegek a promptból szó szerint.
   Mérés: trial_cta_view {hely}, trial_start_click {hely},
   trial_start_blocked {ok}; a trial_started-et az RPC írja.
   ══════════════════════════════════════════════════════════════════════════ */

export const TRIAL_CTA_TEXT = 'Indítsd el a 14 napos PRO-próbát';

const LIST = [
  'Szkenner a top 100 coinra, RSI-áttekintés több idősíkon, Funding, Stratégiák, ETF',
  '22 coin derivatíva-adatai és a Put/Call',
  'Hőtérkép a top 200 coinra',
  '50 aktív riasztás, Telegramon is (ár, tervszint, RSI, funding, OI)',
  'Napi Telegram-összesítő',
];

const REASON = {
  already_used: 'Ehhez a fiókhoz vagy e-mail-címhez már tartozott próba. A terminál teljes tudását Plusszal vagy PRO-val éred el.',
  email_unconfirmed: 'Előbb erősítsd meg az e-mail-címed.',
  disposable: 'Állandó e-mail-címmel indítható.',
};
const GENERIC = 'A próbát most nem sikerült elindítani. Próbáld újra pár perc múlva.';

const seen = new Set(); // egy trial_cta_view helyenként oldalbetöltésenként

export function TrialStartDialog({ hely, user, onClose, onStarted }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState(null);
  const [resent, setResent] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const start = async () => {
    setBusy(true); setReason(null);
    const { data, error } = await supabase.rpc('start_trial');
    setBusy(false);
    if (!error && data?.ok) { onStarted?.(data); onClose(); return; }
    const r = data?.reason || 'error';
    setReason(r);
    trackPaywall('trial_start_blocked', { ok: r, hely }, user);
  };

  const resend = async () => {
    const email = user?.email;
    if (!email) return;
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    setResent(error ? 'Nem sikerült elküldeni. Próbáld újra pár perc múlva.' : 'Elküldtük a megerősítő levelet.');
  };

  return (
    <div className="trial-overlay" role="dialog" aria-modal="true" aria-labelledby="trial-start-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="trial-card">
        <button className="trial-close" aria-label="Bezárás" onClick={onClose}>✕</button>
        <h2 id="trial-start-title" className="trial-title">14 nap PRO-terminál, ingyen</h2>
        <ul className="trial-start-list">
          {LIST.map(t => <li key={t}>{t}</li>)}
        </ul>
        <p className="trial-subtitle">
          Bankkártya nem kell, és a végén nem indul előfizetés. Egyszer indítható, és ha beállítod az első riasztásod, még 7 nappal hosszabb lesz. A próba a terminálra szól: a tanfolyamot és a segédleteket a szinted szerint látod.
        </p>
        {reason && (
          <div className="trial-start-error" role="alert">
            <p>{REASON[reason] || GENERIC}</p>
            {reason === 'email_unconfirmed' && (
              <>
                <button type="button" className="trial-btn trial-btn-ghost" onClick={resend}>Megerősítő levél újraküldése</button>
                {resent && <p className="trial-start-resent" role="status">{resent}</p>}
              </>
            )}
          </div>
        )}
        <div className="trial-block-actions">
          {!reason && (
            <button className="trial-btn trial-btn-purple trial-btn-primary" onClick={start} disabled={busy}>
              {busy ? 'Indítás…' : 'Indítom a próbát'}
            </button>
          )}
          {reason === 'already_used' && (
            <button className="trial-btn trial-btn-plusz trial-btn-primary" onClick={() => openLanding(PLUSZ_PATH, {
              user, event: 'plusz_cta_click', eventProps: { hely: `${hely}_already_used` },
            })}>Plusz megnézése</button>
          )}
          <button className="trial-btn trial-btn-ghost" onClick={onClose}>{reason ? 'Bezárás' : 'Most nem'}</button>
        </div>
      </div>
    </div>
  );
}

/** A „Indítsd el a 14 napos PRO-próbát” gomb a saját megerősítő ablakával.
    Nem jelenik meg, ha a felhasználónak már volt próbája, vagy PRO. */
export default function TrialStartButton({ hely, access, user, onStarted, className = 'trial-start-btn', children }) {
  const [open, setOpen] = useState(false);
  const show = trialCtaFor(access);

  useEffect(() => {
    if (!show || seen.has(hely)) return;
    seen.add(hely);
    trackPaywall('trial_cta_view', { hely }, user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, hely]);

  if (!show) return null;
  return (
    <>
      <button type="button" className={className} onClick={() => { trackPaywall('trial_start_click', { hely }, user); setOpen(true); }}>
        {children || TRIAL_CTA_TEXT}
      </button>
      {open && <TrialStartDialog hely={hely} user={user} onClose={() => setOpen(false)} onStarted={onStarted} />}
    </>
  );
}
